import React, { useState, useEffect, useRef, useCallback } from "react";

interface SearchableDropdownProps {
  placeholder?: string;
  onSearch: (term: string) => Promise<any[]>;
  onSelect: (entity: any) => void;
  formatLabel?: (entity: any) => string;
  createNewLabel?: string;
  onCreateNew?: () => void;
  disabled?: boolean;
  className?: string;
  error?: string;
}

/**
 * SearchableDropdown — Async dropdown for master data selection.
 *
 * Features:
 * - Debounced API search (300ms) as user types
 * - Loading spinner during search
 * - "+ Create new" option (configurable label)
 * - Keyboard navigation (arrow keys, Enter, Escape)
 * - Accessibility: ARIA labels, role="listbox", proper focus management
 * - Click-outside to close
 *
 * Usage:
 * ```tsx
 * <SearchableDropdown
 *   placeholder="Search contacts..."
 *   onSearch={async (term) => {
 *     const response = await api.get('/contacts', {
 *       params: { search: term, buildingId: buildingId }
 *     });
 *     return response.data;
 *   }}
 *   onSelect={(contact) => handleSelectContact(contact)}
 *   formatLabel={(contact) => `${contact.name} (${contact.type})`}
 *   createNewLabel="+ Create new contact"
 *   onCreateNew={() => navigate('/dashboard/contacts/new?return-to=...')}
 * />
 * ```
 */

// Simple debounce helper (no external dependency needed)
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
): T {
  let timeout: NodeJS.Timeout | null = null;
  return ((...args: any[]) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  placeholder = "Search...",
  onSearch,
  onSelect,
  formatLabel,
  createNewLabel = "+ Create new",
  onCreateNew,
  disabled = false,
  className = "",
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search function (debounce inline, no lodash dependency)
  const performSearch = useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setOptions([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const results = await onSearch(term);
        setOptions(results || []);
        setHighlightedIndex(-1);
      } catch (err) {
        console.error("Search error:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [onSearch],
  );

  // Debounced search trigger
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    if (searchTerm) {
      setLoading(true);
      debounceTimerRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, 300);
    } else {
      setOptions([]);
      setLoading(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, performSearch]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === " ") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelectOption(options[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option: any) => {
    onSelect(option);
    setSearchTerm("");
    setIsOpen(false);
    setHighlightedIndex(-1);
    setOptions([]);
  };

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew();
    }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative z-40">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={`block w-full text-sm border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
          } ${error ? "border-red-600" : "border-gray-300"}`}
          aria-expanded={isOpen}
          aria-autocomplete="list"
          role="combobox"
        />

        {/* Loading spinner */}
        {loading && (
          <div className="absolute right-3 top-2.5">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}

        {/* Dropdown chevron */}
        {!loading && (
          <div className="absolute right-3 top-2.5 pointer-events-none text-gray-400">
            <svg
              className={`h-4 w-4 transition-transform ${
                isOpen ? "transform rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl">
          <ul ref={listRef} role="listbox" className="max-h-60 overflow-y-auto">
            {/* Create new option */}
            {onCreateNew && (
              <li
                key="create-new"
                onClick={handleCreateNew}
                className="px-3 py-2 bg-blue-50 hover:bg-blue-100 cursor-pointer text-blue-700 font-medium border-b border-gray-200"
                role="option"
              >
                {createNewLabel}
              </li>
            )}

            {/* Empty state */}
            {options.length === 0 && !loading && searchTerm && (
              <li className="px-3 py-2 text-gray-500 text-sm">
                No results found for "{searchTerm}"
              </li>
            )}

            {/* Search prompt */}
            {options.length === 0 && !loading && !searchTerm && (
              <li className="px-3 py-2 text-gray-500 text-sm">
                Start typing to search...
              </li>
            )}

            {/* Option list */}
            {options.map((option, index) => (
              <li
                key={option.id || index}
                onClick={() => handleSelectOption(option)}
                className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  highlightedIndex === index
                    ? "bg-blue-100 text-blue-900"
                    : "hover:bg-gray-50"
                }`}
                role="option"
              >
                <div className="text-sm">
                  {formatLabel
                    ? formatLabel(option)
                    : option.name || JSON.stringify(option)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
